import { addDoc, collection, doc, increment, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { PUSH_NOTIFICATION_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";

const determineMsgType = (msgType: string, additionalDetails: any) => {
  switch (msgType) {
    case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
      return {
        msgTitle: `You've been removed from a job.`,
        msgBody: `Scheduler: ${additionalDetails?.schedFName || additionalDetails}`,
      };

    case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
      return {
        msgTitle: `You've been added to a job!`,
        msgBody: `Scheduler: ${additionalDetails.schedFName}`,
      };

    case PUSH_NOTIFICATION_TYPES.DirectMessage:
      return {
        msgTitle: additionalDetails.senderName,
        msgBody: additionalDetails.msgPreview,
      };

    case PUSH_NOTIFICATION_TYPES.JobMessage:
      return {
        msgTitle: additionalDetails.jobName,
        msgBody: additionalDetails.msgPreview,
      };

    case PUSH_NOTIFICATION_TYPES.AssociationRequest:
      return {
        msgTitle: "Associate Request",
        msgBody: additionalDetails.fromFName + " wants to Connect!",
      };

    case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
      return {
        msgTitle: "Job is filled",
        msgBody: additionalDetails.shiftIdentifier + " is filled!",
      };

    case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
      return {
        msgTitle: "Job is no longer filled",
        msgBody: additionalDetails.shiftIdentifier + " is no longer filled!",
      };

    case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
      return {
        msgTitle: additionalDetails.schedFName + "'s Job Is Still Available",
        msgBody: additionalDetails.jobPay,
      };

    case PUSH_NOTIFICATION_TYPES.SchedulerEditedJob:
      return {
        msgTitle: "Job updated by " + additionalDetails.schedFName,
        msgBody: additionalDetails.company + " - " + additionalDetails.pay,
      };

    case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
      return {
        msgTitle: "New Job Available From " + additionalDetails.schedFName,
        msgBody: additionalDetails.jobPay,
      };

    case PUSH_NOTIFICATION_TYPES.WerkerPaymentReceived:
      return {
        msgTitle: "You've been paid!",
        msgBody: `You've been paid by ${additionalDetails.schedulerFName}`,
      };

    case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite:
      return {
        msgTitle: "New Werker joined!",
        msgBody: `${additionalDetails.werkerFName} ${additionalDetails.werkerLName} joined your group and is ready to Werk!`,
      };

    default:
      return {
        msgTitle: "",
        msgBody: "",
      };
  }
};

const shouldWriteToNotificationLog = (msgType: string) => {
  switch (msgType) {
    case PUSH_NOTIFICATION_TYPES.DirectMessage:
    case PUSH_NOTIFICATION_TYPES.JobMessage:
    case PUSH_NOTIFICATION_TYPES.WerkerPaymentReceived:
      // Payment receipts appear in the dedicated Receipts tab — not the standard notifications list
      return false;

    case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
      // New-job availability is surfaced in the Job Feed; skip the in-app Notifications list entry
      return false;

    default:
      return true;
  }
};

export async function fx_FB_SendNotification(
  fromUID: string,
  toUID: string[],
  msgType: string,
  additionalDetails: any
) {
  const msgContents = determineMsgType(msgType, additionalDetails);
  const writeToNotificationLog = shouldWriteToNotificationLog(msgType);

  try {
    // Queue push notification for GCF listener
    const jobNotificationRef = collection(db, FB_COLLECTION_PATH.Users);
    await addDoc(collection(jobNotificationRef, fromUID, "notificationpending"), {
      NotificationInfo: {
        sendTo: toUID,
        title: msgContents.msgTitle,
        body: msgContents.msgBody,
        type: msgType,
        additionalDetails: additionalDetails || null,
      },
    });

    // For each recipient:
    // 1) increment app badge count
    // 2) optionally write to notification log (non-chat only)
    await Promise.all(
      toUID.map(async (element) => {
        try {
          // Existing app tile / push badge count
          const userRef2 = doc(db, `users/${element}/users_BadgeCount`, element);
          await setDoc(
            userRef2,
            {
              badgeCount: increment(1),
              uid: element,
            },
            { merge: true }
          );

          // Write in-app notification log entry for non-chat notifications only
          if (writeToNotificationLog) {
            await addDoc(collection(db, `users/${element}/notifications`), {
              type: msgType,
              title: msgContents.msgTitle,
              body: msgContents.msgBody,
              createdAt: serverTimestamp(),
              isRead: false,
              readAt: null,
              fromUID: fromUID,
              toUID: element,
              additionalDetails: additionalDetails || null,
            });

            // Dedicated Notifications page unread count
            const notificationsCountRef = doc(db, `users/${element}/users_NotificationsCount`, element);

            await setDoc(
              notificationsCountRef,
              {
                notificationsCount: increment(1),
                uid: element,
              },
              { merge: true }
            );
          }
        } catch (error) {
          // console.log(error);
        }
      })
    );
    return { status: "sent" };
  } catch (error) {
    // console.log(error);
    return { status: "error", error };
  }
}

/**
 * Push types currently handled here:
 * - DirectMessage                -> push only, no notification log
 * - JobMessage                   -> push only, no notification log
 * - AssociationRequest           -> push + notification log
 * - SchedulerFilledJobs          -> push + notification log
 * - SchedulerUnFilledJobs        -> push + notification log
 * - WerkerJobReminder            -> push + notification log
 * - WerkerJobAvailable           -> push only, no notification log
 * - SchedulerAddedWerkerToShift  -> push + notification log
 * - SchedulerRemovedWerkerFromShift -> push + notification log
 */
